import React from 'react';
import Link from 'next/link';
import { BCorpLogo } from '@/components/svgs/BCorpLogo';
import { PrimaryProofPointProps, SecondaryProofPointProps } from '@/components/WhyAthena/WhyAthena';
import { AthenaLogo } from '@/components/svgs/AthenaLogo';
import { SuperScript } from '@/components/cohort/SuperScript';

export const WHY_ATHENA_REFINANCING_CONTENT: Array<PrimaryProofPointProps> = [
  {
    title: 'Fiercely fair value',
    paragraph: (
      <>
        Changing home loans for good is in our DNA and can save you money. So far we&apos;ve saved thousands of Aussies
        a total of $704m
        <SuperScript>**</SuperScript> and counting.
      </>
    ),
    buttonText: 'More about fiercely fair value',
    modalContent: [
      {
        title: <h3>Aussie-firsts on Straight Up and Power Up loans</h3>,
      },
      {
        title: 'No Athena fees',
        body: 'No application fees. No ongoing fees. No discharge fees. Zero. Zilch. Nada.',
      },
      {
        title: 'Drop your own rate with AcceleRATES',
        body: (
          <>
            We&apos;ll automatically drop your rate as you pay down your home loan
            <SuperScript>2</SuperScript>.
          </>
        ),
      },
      {
        title: 'No loyalty tax',
        body: (
          <>
            We always give existing and new customers the same sexy rates for like-for-like loans
            <SuperScript>3</SuperScript>. We&apos;ll have your back.
          </>
        ),
      },
    ],
    imageAlt: 'Customer Savings 60k',
    imageSrcDesktop: '/next-assets/images/cohort/why-cust-savings-60k-lg.png',
    imageSrcMobile: '/next-assets/images/cohort/why-cust-savings-60k-sm.png',
  },
  {
    title: 'Need to borrow more?',
    paragraph: 'Unlock equity easily and get maximum borrowing power.',
    buttonText: 'More about max borrowing power',
    modalContent: [
      {
        title: 'Need to borrow more?',
        body: (
          <>
            Maximise your borrowing power by accessing the usable equity in your property.{' '}
            <Link href="/calculators/equity">Calculate yours now</Link> with our equity calculator.
            <br />
            <br />
            Athena&apos;s borrower friendly policies mean we can generally lend more. Get in touch with our Loan Experts
            now or <Link href="/calculators/buying">check out our calculator</Link> to assess your borrowing power.
          </>
        ),
      },
    ],
    imageAlt: 'Repayments after accessing equity',
    imageSrcDesktop: '/next-assets/images/cohort/why-unlock-equity-lg.svg',
    imageSrcMobile: '/next-assets/images/cohort/why-unlock-equity-sm.svg',
  },
  {
    title: 'Flexibility without the fees',
    paragraph:
      'Easily change your loan type, repayment frequency or top-up when you need, for free, on our Straight Up and Power Up loans.',
    buttonText: 'More about our flexible options',
    modalContent: [
      {
        title: 'Want flexibility without the fees?',
        body: "We believe in giving you more power over your loan. Change your Straight Up or Power Up loan as life changes. Need to increase cash flow? Easily switch to Interest Only for a period. Want to access cash? We can organise fast top-ups. Make changes to your repayments easily in our app. And most changes have zero fees. Now that's fair.",
      },
    ],
    imageAlt: 'Switch Interest Rate type',
    imageSrcDesktop: '/next-assets/images/cohort/why-switch-pi-io-lg.svg',
    imageSrcMobile: '/next-assets/images/cohort/why-switch-pi-io-sm.svg',
  },
  {
    title: 'Offset and save',
    paragraph:
      "With Athena's offset accounts, you can stash your cash any way you want with our Power Up loan. The more money you put in your offset, the less interest you'll pay.",
    buttonText: 'More about offsetting your interest',
    modalContent: [
      {
        title: 'Offset and save',
        body: (
          <>
            Set up your offsets (up to 10) and stash your cash to reduce the interest payable on your loan. You only pay
            interest based on the net loan balance after taking away the total of your offset balances attached to that
            loan.
            <br />
            <br />
            Salary credit into your offset account to maximise your interest savings.
            <br />
            <br />
            Set and forget with scheduled payments to your transaction or credit card accounts.
          </>
        ),
      },
    ],
    imageAlt: 'List of offset accounts',
    imageSrcDesktop: '/next-assets/images/cohort/why-offsets-lg.png',
    imageSrcMobile: '/next-assets/images/cohort/why-offsets-sm.png',
  },
  {
    title: 'Hedge your bets or consolidate debts',
    paragraph:
      'Customise your split loans across fixed, variable, owner, investor, P&I and IO or different loan terms (max 10). Consolidate up to four debts onto the one interest rate.',
    buttonText: 'More about our powerful features',
    modalContent: [
      {
        title: 'Hedge your bets',
        body: "Split your loan to be part variable and part fixed. Best of both worlds, you have peace of mind your interest rates and repayments won't change during your fixed rate period. If variable rates drops, only your variable rate loan will benefit from this.",
      },
      {
        title: 'Consolidate debt and save',
        body: 'You could save yourself a ton of money and stress, by transferring debt you have on higher interest rates into a split loan secured against your property and paying down that loan sooner.',
      },
    ],
    imageAlt: 'Consolidate your loans',
    imageSrcDesktop: '/next-assets/images/cohort/why-consolidate-lg.png',
    imageSrcMobile: '/next-assets/images/cohort/why-consolidate-sm.png',
  },
  {
    title: 'Self-employed or want to avoid LMI?',
    paragraph: (
      <>
        Our Tailored loans are custom-made for you. Our lite doc loan makes it easy for self-employed borrowers who need
        alternative income verification options. For those wanting to borrow more than 80% of their home’s value without
        paying LMI, we’ve got you covered with our 80-85% LVR loan.
      </>
    ),
    buttonText: 'More about our Tailored loans',
    buttonLink: '/tailored',
    modalContent: null,
    imageAlt: '',
    imageSrcDesktop: '/next-assets/images/cohort/tailored-cohort-image.svg',
    imageSrcMobile: '/next-assets/images/cohort/tailored-cohort-image.svg',
  },
];

export const WHY_ATHENA_SECONDARY_PROOF_POINTS: Array<SecondaryProofPointProps> = [
  {
    image: <BCorpLogo />,
    title: 'Doing business for good',
    body: "Balancing profit with people and purpose, Athena is a certified B-Corp. Proof we're committed to a better, fairer world. B-Corps are businesses that meet high standards of social and environmental performance, accountability, and transparency.",
  },
  {
    image: <AthenaLogo />,
    title: 'The home loan wrecker',
    body: "Since our launch in 2019 we've settled over $6.3 billion in loans for Aussies. Home loans are all we do, and we're obsessed with changing them for good. We continue to rewrite the rules, creating tools, products and policy that put the power back in our customers' hands.",
  },
];
